import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Form, Formik } from 'formik';
import { object, number, string } from 'yup';

import Button from 'components/common/forms/Button';
import ButtonLoading from 'components/common/forms/ButtonLoading';
import FormGroupOld from 'components/common/forms/FormGroup';
import FormInput from 'components/common/forms/FormInput';
import Modal, { ModalHeader } from 'components/common/Modal';
import { useAuth } from 'contexts/auth';
import { useChargeOrganization } from 'hooks/backend/billing';
import { showErrorMessage, showSuccessMessage } from 'util/notifications';

interface BuyCreditsFormData {
  amount: number | '';
  email: string;
  description: string | '';
}

const BuyCreditsSchema = object().shape({
  amount: number()
    .typeError('Please enter a valid amount.')
    .required('Please enter an amount.')
    .min(1, 'Minimum purchase amount is $1.00')
    .max(50000, 'Maximum purchase amount is $50,000.00'),
  email: string()
    .email('Please provide a valid email.')
    .required('Please provide your email.'),
});

export const BuyCreditsModal = ({
  onCloseModal,
  organizationId,
}: {
  onCloseModal(): void;
  organizationId: number;
}): JSX.Element => {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();

  const { isPending: isCharging, mutate: chargeOrganization } =
    useChargeOrganization({
      onError: (err) => {
        showErrorMessage({ err });
      },
      onSuccess: (data) => {
        showSuccessMessage(
          `Successfully added ${data.creditsAdded} in credits.`,
        );
        onCloseModal();
      },
    });

  return (
    <Modal
      header={
        <ModalHeader onClickClose={onCloseModal}>Purchase credits</ModalHeader>
      }
      onCloseModal={onCloseModal}
      position="top"
    >
      <Formik<BuyCreditsFormData>
        initialValues={{
          amount: '',
          email: user?.email ?? '',
          description: 'Purchase of credits through Stripe',
        }}
        onSubmit={async (formData) => {
          const cardElement = elements?.getElement('card');
          if (!stripe || !cardElement) {
            showErrorMessage({
              message:
                'Could not load credit card payment processor. Please refresh the page and try again.',
            });
            return;
          }

          const { error, token } = await stripe.createToken(cardElement);
          if (error) {
            showErrorMessage({
              message: error.message ?? 'Your card could not be processed.',
            });
            return;
          }
          if (!token?.id) {
            showErrorMessage({
              message: 'Your card could not be processed. Please try again.',
            });
            return;
          }

          chargeOrganization({
            organizationId,
            amount: Math.round(Number(formData.amount) * 100),
            email: formData.email,
            stripeToken: token.id,
            description: formData.description || undefined,
          });
        }}
        validateOnChange={false}
        validationSchema={BuyCreditsSchema}
      >
        <Form className="space-y-4">
          <FormInput
            icon={<span className="text-gray-d-500">$</span>}
            iconPlacement="leading"
            id="amount"
            label="Amount to purchase"
            labelFor="amount"
            name="amount"
            size="md"
            step="0.01"
            type="number"
          />
          <FormInput
            id="email"
            label="Receipt Email"
            labelFor="email"
            name="email"
            size="md"
            type="email"
          />
          <FormGroupOld label="Card Info" labelFor="stripe-card-element">
            <div className="w-full rounded-lg border border-gray-d-300 shadow-sm py-2 px-3">
              <CardElement id="stripe-card-element" />
            </div>
          </FormGroupOld>
          <FormInput
            id="description"
            label="Transaction Description"
            labelFor="description"
            name="description"
            size="md"
            type="text"
          />
          <div className="flex gap-4">
            <Button
              grow
              hierarchy="secondary-gray"
              onClick={onCloseModal}
              size="lg"
              type="button"
            >
              Cancel
            </Button>
            <ButtonLoading
              grow
              hierarchy="primary"
              isLoading={isCharging}
              size="lg"
              type="submit"
            >
              Purchase
            </ButtonLoading>
          </div>
        </Form>
      </Formik>
    </Modal>
  );
};
